import { useParams } from "next/navigation";
import { useAuth } from "@/lib/auth";

/** Prefix a flat app path with the org slug: orgHref("acme", "/envelopes/1") → "/o/acme/envelopes/1". */
export function orgHref(slug: string, path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `/o/${slug}${p}`;
}

/** Routes that live OUTSIDE any workspace and must never be org-prefixed. */
const PUBLIC_PREFIXES = ["/sign/", "/verify/", "/bulk-sign/", "/templates/", "/login", "/register", "/admin"];

/**
 * Defensive re-home for a URL Amanda emitted (spec §5.1). The backend builds
 * every workspace URL through org_url, so a flat path here is a bug upstream:
 * it is prefixed with the CURRENT tab's slug (never the localStorage default
 * the middleware would pick) and logged, so it cannot silently land in the
 * wrong workspace. Already-prefixed, public and external URLs pass through.
 */
export function ensureOrgHref(slug: string | null | undefined, url: string): string {
  if (!url.startsWith("/") || url.startsWith("//") || url.startsWith("/o/")) return url;
  if (PUBLIC_PREFIXES.some((p) => url === p.replace(/\/$/, "") || url.startsWith(p))) return url;
  if (!slug) return url;
  if (typeof console !== "undefined") console.warn(`[amanda] flat navigate_to ${url} re-homed to /o/${slug}`);
  return orgHref(slug, url);
}

/**
 * Returns a prefixer bound to the current tab's org slug (per-tab correctness).
 * Slug source: the /o/[org] URL param (per-tab) → activeOrg fallback.
 * GRACEFUL DEGRADE: if no slug is resolvable (e.g. an anonymous public render),
 * returns the FLAT path unchanged — middleware 307s it for logged-in users and
 * anonymous users get sent through /o→/login. So this is SAFE to use even in
 * components that sometimes render outside a workspace. Never emits "/o//…".
 */
export function useOrgHref() {
  const params = useParams();
  const { activeOrg } = useAuth();
  const slug = (params?.org as string | undefined) ?? activeOrg?.slug;
  return (path: string) => (slug ? orgHref(slug, path) : path);
}
